import React, { useState } from "react";
import { Button, Input, Alert } from "@heroui/react";
import { Eye, EyeOff } from "../../components/admin/DashboardIcons.jsx";

const ChangePassword = () => {
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleNewPasswordChange = (e) => {
    setNewPassword(e.target.value);
  };

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
  };

  const handleChangePassword = async () => {
    setLoading(true);
    setMessage("");
    setError("");

    if (newPassword.length < 6 || confirmPassword.length < 6) {
      setErrorMessage(
        "New password and confirm password must be at least 6 characters"
      );
      setShowErrorAlert(true);
      setLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorMessage("New password and confirm password do not match");
      setShowErrorAlert(true);
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}/change-password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + localStorage.getItem("jwt"),
          },
          body: JSON.stringify({
            old_password: oldPassword,
            new_password: newPassword,
            confirm_password: confirmPassword,
            userId: localStorage.getItem("uid"),
          }),
        }
      );

      const result = await response.json();

      if (response.status === 200) {
        if (result.success) {
          setAlertMessage(result.message);
          setShowAlert(true);
          setShowErrorAlert(false);
          setOldPassword("");
          setNewPassword("");
          setConfirmPassword("");
          setTimeout(() => {
            setShowAlert(false);
            setAlertMessage("");
          }, 3000);
        } else {
          setErrorMessage(result.message || "Old password is incorrect");
          setShowErrorAlert(true);
        }
      } else if (response.status === 401) {
        setErrorMessage(result.message || "Unauthorized");
        setShowErrorAlert(true);
      } else if (response.status === 400) {
        setErrorMessage(result.message || "Bad request");
        setShowErrorAlert(true);
      } else if (response.status === 404) {
        setErrorMessage(result.message || "User not found");
        setShowErrorAlert(true);
      } else {
        setErrorMessage(result.message || "Something went wrong");
        setShowErrorAlert(true);
      }
    } catch (err) {
      console.error(err);
      setErrorMessage("Something went wrong");
      setShowErrorAlert(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white shadow rounded-lg p-6 mb-6">
      {showAlert && (
        <Alert
          color="success"
          className="mb-4"
          onClose={() => setShowAlert(false)}
        >
          {alertMessage}
        </Alert>
      )}
      {showErrorAlert && (
        <Alert
          color="danger"
          className="mb-4"
          onClose={() => setShowErrorAlert(false)}
        >
          {errorMessage}
        </Alert>
      )}
      <h3 className="text-lg font-medium text-gray-900 mb-4">Change Password</h3>

      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          handleChangePassword();
        }}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Old Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Old Password <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Input
                type={showOld ? "text" : "password"}
                placeholder="Enter Old Password"
                className="border border-gray-300 rounded-xl pr-10"
                autoComplete="off"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                required
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                onClick={() => setShowOld(!showOld)}
              >
                {showOld ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {/* New Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              New Password <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Input
                type={showNew ? "text" : "password"}
                placeholder="Enter New Password"
                className="border border-gray-300 rounded-xl pr-10"
                value={newPassword}
                onChange={handleNewPasswordChange}
                required
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                onClick={() => setShowNew(!showNew)}
              >
                {showNew ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Confirm Password <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Input
                type={showConfirm ? "text" : "password"}
                placeholder="Enter Confirm Password"
                className="border border-gray-300 rounded-xl pr-10"
                autoComplete="off"
                value={confirmPassword}
                onChange={handleConfirmPasswordChange}
                required
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                onClick={() => setShowConfirm(!showConfirm)}
              >
                {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
        </div>

        {message && <p className="text-green-600 mt-2">{message}</p>}
        {error && <p className="text-red-600 mt-2">{error}</p>}

        <div className="flex justify-end space-x-3 mt-6">
          <Button
            color="danger"
            variant="light"
            onClick={() => {
              setOldPassword("");
              setNewPassword("");
              setConfirmPassword("");
              setMessage("");
              setError("");
            }}
          >
            Cancel
          </Button>
          <Button type="submit" className="bg-blue-600 text-white" disabled={loading}>
            {loading ? "Updating..." : "Change Password"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ChangePassword;
